// 📄 pages/Admin/sections/UsersSection.tsx
// Sub-fase de seguimiento (2026-08-11): esta vista quedó separada a
// propósito de la gestión de miembros del día a día. Antes listaba TODA
// la colección `users` (cualquier consultor que alguna vez inició sesión)
// con un <select> Consultor/Admin por fila, sin ninguna fricción — un
// error de click cambiaba a cualquiera a Admin de plataforma (acceso
// total al sistema). A medida que crezca la cantidad de consultores esa
// lista además se vuelve inmanejable.
//
// Ahora: SOLO admins de plataforma (listPlatformAdmins, query filtrada,
// no un listado completo). Asignar/crear/revocar admin son 3 acciones
// deliberadas con confirmación escribiendo el email — mismo nivel de
// fricción que ya se usa en otras acciones sensibles de esta plataforma.
// La gestión de "quién entra a un equipo, con qué rol" para el día a día
// (Owner/Admin de ORG, Miembro, Invitado) vive en "Gestionar miembros",
// accesible desde el switcher de organización — ver GestionarMiembrosModal.tsx.
import React, { useEffect, useState } from 'react';
import { RefreshCw, ShieldCheck, ShieldPlus, ShieldMinus, UserCog, AlertTriangle } from 'lucide-react';
import {
  UserProfile,
  listPlatformAdmins,
  findPlatformUserByEmail,
  setUserRole,
  createPlatformAdminAccount
} from '../../../services/userService.ts';
import { useAuth } from '../../../context/AuthContext.tsx';

type PasoAsignar = 'buscar' | 'confirmarExistente' | 'crearNuevo' | 'confirmarNuevo';

export default function UsersSection() {
  const { user: currentUser } = useAuth();
  const [admins, setAdmins] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ── Revocar (con confirmación escribiendo el email) ──
  const [revocando, setRevocando] = useState<UserProfile | null>(null);
  const [confirmRevocar, setConfirmRevocar] = useState('');
  const [guardandoRevocar, setGuardandoRevocar] = useState(false);

  // ── Asignar / crear admin nuevo ──
  const [modalAbierto, setModalAbierto] = useState(false);
  const [paso, setPaso] = useState<PasoAsignar>('buscar');
  const [email, setEmail] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [encontrado, setEncontrado] = useState<UserProfile | null>(null);
  const [confirmAsignar, setConfirmAsignar] = useState('');
  const [nombreCompleto, setNombreCompleto] = useState('');
  const [passwordTemporal, setPasswordTemporal] = useState('');
  const [guardandoAsignar, setGuardandoAsignar] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      setAdmins(await listPlatformAdmins());
    } catch (err) {
      console.error('Error cargando admins:', err);
      setError('No se pudieron cargar los administradores.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const cerrarModal = () => {
    setModalAbierto(false);
    setPaso('buscar');
    setEmail('');
    setEncontrado(null);
    setConfirmAsignar('');
    setNombreCompleto('');
    setPasswordTemporal('');
  };

  const handleBuscar = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalizado = email.trim().toLowerCase();
    if (!normalizado) return;

    setBuscando(true);
    setError(null);
    try {
      const found = await findPlatformUserByEmail(normalizado);
      if (found) {
        if (found.role === 'admin') {
          setError('Esa cuenta ya es Admin de plataforma.');
          return;
        }
        setEncontrado(found);
        setPaso('confirmarExistente');
      } else {
        setPaso('crearNuevo');
      }
    } catch (err) {
      console.error('Error buscando usuario:', err);
      setError('Error al buscar el usuario.');
    } finally {
      setBuscando(false);
    }
  };

  const handleConfirmarAsignarExistente = async () => {
    if (!encontrado || confirmAsignar.trim().toLowerCase() !== encontrado.email.toLowerCase()) return;
    setGuardandoAsignar(true);
    setError(null);
    try {
      await setUserRole(encontrado.uid, 'admin');
      setSuccess(`${encontrado.email} ahora es Admin de plataforma.`);
      cerrarModal();
      await loadData();
    } catch (err) {
      console.error('Error asignando admin:', err);
      setError('Error al asignar el rol de Admin.');
    } finally {
      setGuardandoAsignar(false);
    }
  };

  const handleCrearNuevo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombreCompleto.trim()) { setError('El nombre completo es obligatorio.'); return; }
    if (passwordTemporal.length < 6) { setError('La contraseña temporal debe tener al menos 6 caracteres.'); return; }
    setError(null);
    setPaso('confirmarNuevo');
  };

  const handleConfirmarCrearNuevo = async () => {
    if (confirmAsignar.trim().toLowerCase() !== email.trim().toLowerCase()) return;
    setGuardandoAsignar(true);
    setError(null);
    try {
      await createPlatformAdminAccount({
        email: email.trim().toLowerCase(),
        nombreCompleto: nombreCompleto.trim(),
        passwordTemporal
      });
      setSuccess(`Se creó la cuenta de ${email} como Admin de plataforma. Deberá cambiar su contraseña en el primer inicio de sesión.`);
      cerrarModal();
      await loadData();
    } catch (err: any) {
      console.error('Error creando admin:', err);
      setError(err.message || 'Error al crear la cuenta de Admin.');
    } finally {
      setGuardandoAsignar(false);
    }
  };

  const handleConfirmarRevocar = async () => {
    if (!revocando || confirmRevocar.trim().toLowerCase() !== revocando.email.toLowerCase()) return;
    setGuardandoRevocar(true);
    setError(null);
    try {
      await setUserRole(revocando.uid, 'consultant');
      setSuccess(`${revocando.email} ya no es Admin de plataforma.`);
      setRevocando(null);
      setConfirmRevocar('');
      await loadData();
    } catch (err) {
      console.error('Error revocando admin:', err);
      setError('Error al revocar el rol de Admin.');
    } finally {
      setGuardandoRevocar(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light text-white">Gestión de Usuarios</h1>
          <p className="text-sm text-gray-400 mt-1">
            Administradores de la plataforma — acceso total al sistema. Los miembros del día a día (Owner/Admin/Miembro de una organización) se gestionan desde "Gestionar miembros" en el selector de organización.
          </p>
        </div>
        <button
          onClick={() => setModalAbierto(true)}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all shadow-lg flex-shrink-0"
        >
          <ShieldPlus className="w-4 h-4" /> Asignar / Crear Admin
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-sm">{error}</div>
      )}
      {success && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-3 rounded-lg text-sm">{success}</div>
      )}

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
          <p className="text-sm">Cargando administradores...</p>
        </div>
      ) : admins.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">No hay administradores de plataforma.</p>
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-white/5 text-gray-400 text-[11px] uppercase tracking-wider">
                <th className="p-4">Email</th>
                <th className="p-4 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {admins.map((u) => {
                const isSelf = u.uid === currentUser?.uid;
                return (
                  <tr key={u.uid} className="border-t border-white/5 hover:bg-white/[0.03] transition-all">
                    <td className="p-4 text-white">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                        {u.name ? `${u.name} — ${u.email}` : u.email}
                        {isSelf && (
                          <span className="text-[10px] bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 px-1.5 py-0.5 rounded-full">Tú</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => { setRevocando(u); setConfirmRevocar(''); setError(null); }}
                        disabled={admins.length <= 1}
                        title={admins.length <= 1 ? 'No podés revocar al único admin de plataforma' : 'Revocar Admin'}
                        className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-all ml-auto"
                      >
                        <ShieldMinus className="w-3.5 h-3.5" /> Revocar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal: revocar admin (confirmar escribiendo el email) ── */}
      {revocando && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-medium text-white">Revocar Admin de plataforma</h3>
                <p className="text-sm text-gray-400 mt-1">
                  <strong className="text-white">{revocando.email}</strong> va a perder acceso total al sistema (queda como Consultor normal). Esta acción se puede revertir volviendo a asignarlo.
                </p>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                Escribí <span className="text-white font-mono">{revocando.email}</span> para confirmar
              </label>
              <input
                autoFocus
                value={confirmRevocar}
                onChange={(e) => setConfirmRevocar(e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-red-500"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setRevocando(null); setConfirmRevocar(''); }}
                className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmarRevocar}
                disabled={guardandoRevocar || confirmRevocar.trim().toLowerCase() !== revocando.email.toLowerCase()}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-500 disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
              >
                {guardandoRevocar ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldMinus className="w-4 h-4" />}
                Revocar Admin
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: asignar / crear admin ── */}
      {modalAbierto && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="text-lg font-medium text-white flex items-center gap-2">
                <UserCog className="w-5 h-5 text-indigo-400" /> Asignar / Crear Admin
              </h3>
              <button onClick={cerrarModal} className="text-gray-400 hover:text-white">✕</button>
            </div>

            <div className="p-5 space-y-4">
              {paso === 'buscar' && (
                <form onSubmit={handleBuscar} className="space-y-3">
                  <p className="text-xs text-gray-500">Buscamos primero si ya existe una cuenta con ese email.</p>
                  <input
                    type="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@empresa.com"
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                    required
                  />
                  <button
                    type="submit"
                    disabled={buscando}
                    className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all"
                  >
                    {buscando ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Buscar'}
                  </button>
                </form>
              )}

              {paso === 'confirmarExistente' && encontrado && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 bg-amber-500/5 border border-amber-500/20 rounded-xl p-3">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-200">
                      <strong>{encontrado.email}</strong> ya tiene cuenta en OneHub. Va a pasar a tener acceso total a la plataforma.
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">
                      Escribí <span className="text-white font-mono">{encontrado.email}</span> para confirmar
                    </label>
                    <input
                      autoFocus
                      value={confirmAsignar}
                      onChange={(e) => setConfirmAsignar(e.target.value)}
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                  <button
                    onClick={handleConfirmarAsignarExistente}
                    disabled={guardandoAsignar || confirmAsignar.trim().toLowerCase() !== encontrado.email.toLowerCase()}
                    className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all"
                  >
                    {guardandoAsignar ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldPlus className="w-4 h-4" />}
                    Asignar Admin
                  </button>
                </div>
              )}

              {paso === 'crearNuevo' && (
                <form onSubmit={handleCrearNuevo} className="space-y-3">
                  <p className="text-xs text-gray-500">
                    No existe ninguna cuenta con <strong className="text-white">{email}</strong>. Se va a crear directamente como Admin de plataforma.
                  </p>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Nombre completo</label>
                    <input
                      autoFocus
                      value={nombreCompleto}
                      onChange={(e) => setNombreCompleto(e.target.value)}
                      placeholder="Nombre y apellido"
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Contraseña temporal</label>
                    <input
                      type="password"
                      value={passwordTemporal}
                      onChange={(e) => setPasswordTemporal(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                      required
                      minLength={6}
                    />
                    <p className="text-[11px] text-gray-500 mt-1">Se le pedirá cambiarla al iniciar sesión por primera vez.</p>
                  </div>
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all"
                  >
                    Continuar
                  </button>
                </form>
              )}

              {paso === 'confirmarNuevo' && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 bg-amber-500/5 border border-amber-500/20 rounded-xl p-3">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-200">
                      Se va a crear <strong>{email}</strong> ({nombreCompleto}) como cuenta nueva, con acceso total a la plataforma desde el primer login.
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">
                      Escribí <span className="text-white font-mono">{email}</span> para confirmar
                    </label>
                    <input
                      autoFocus
                      value={confirmAsignar}
                      onChange={(e) => setConfirmAsignar(e.target.value)}
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                  <button
                    onClick={handleConfirmarCrearNuevo}
                    disabled={guardandoAsignar || confirmAsignar.trim().toLowerCase() !== email.trim().toLowerCase()}
                    className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all"
                  >
                    {guardandoAsignar ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldPlus className="w-4 h-4" />}
                    Crear cuenta Admin
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
